import React, { useState } from 'react';
import { TouchableOpacity } from 'react-native';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { useFormContext } from 'react-hook-form';

import type { PlayerProgressItem } from '@/features/player/components/types';

import { MutatePlayerLevelTab } from './mutate-player-level-tab';
import type { MutatePlayerForm } from './types';

type TabPillProps = {
  label: string;
  isActive: boolean;
  onPress: () => void;
};

function TabPill({ label, isActive, onPress }: TabPillProps) {
  const styles = useStyles();
  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={onPress}
      style={[styles.tabPill, isActive && styles.tabPillActive]}>
      <Text
        variant={!isActive ? 'Font Inter/Subtitle/Medium 12px' : 'Font Inter/Subtitle/Bold 12px'}
        style={[styles.tabPillText, isActive && styles.tabPillTextActive]}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}

export function MutatePlayerEditTabs({
  profileContent,
  playerId,
  playerDisplayName,
  progressItems,
  initialProfileLevelValue,
}: {
  profileContent: React.ReactNode;
  playerId: string;
  playerDisplayName: string;
  progressItems: PlayerProgressItem[];
  /** e.g. "level_8" from Firestore/profile; used to avoid hydration timing issues */
  initialProfileLevelValue?: string | null;
}) {
  const { scale } = useScaling();
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<'profile' | 'level'>('profile');
  useFormContext<MutatePlayerForm>(); // keep inside FormProvider

  return (
    <Stack width="100%" gap={scale({ phone: 18, tablet: 24 })}>
      <Stack direction="row" style={styles.tabsContainer}>
        <TabPill
          label={STRINGS.player.mutatePlayer.levelTab.tabProfile}
          isActive={activeTab === 'profile'}
          onPress={() => setActiveTab('profile')}
        />
        <TabPill
          label={STRINGS.player.mutatePlayer.levelTab.tabLevel}
          isActive={activeTab === 'level'}
          onPress={() => setActiveTab('level')}
        />
      </Stack>

      {/* Keep both tabs mounted so tab switching doesn't reset internal state (only clear on back/unmount). */}
      <Stack
        gap={scale({ phone: 18, tablet: 24 })}
        style={activeTab === 'profile' ? styles.tabVisible : styles.tabHidden}>
        {profileContent}
      </Stack>
      <Stack style={activeTab === 'level' ? styles.tabVisible : styles.tabHidden}>
        <MutatePlayerLevelTab
          playerId={playerId}
          playerDisplayName={playerDisplayName}
          progressItems={progressItems}
          initialProfileLevelValue={initialProfileLevelValue}
        />
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  tabsContainer: {
    borderRadius: scale(30),
    padding: scale(2),
    alignSelf: 'center',
    width: isTablet ? scale(343) : '100%',
    backgroundColor: colors['3'],
    height: scale({ phone: 36, tablet: 40 }),
  },
  tabPill: {
    flex: 1,
    borderRadius: scale(30),
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabPillActive: {
    backgroundColor: 'white',
  },
  tabPillText: {
    color: colors['10'],
    fontSize: scale({ phone: 12, tablet: 14 }),
  },
  tabPillTextActive: {
    color: colors.Blue,
  },
  tabVisible: {
    display: 'flex',
  },
  tabHidden: {
    display: 'none',
  },
}));
